import { yupResolver } from '@hookform/resolvers/yup';
import {
    Box,
    Button,
    Divider,
    Stack,
    Step,
    StepButton,
    Stepper,
    Typography,
} from '@mui/material';
import BaseDialog from 'common/components/base-dialog';
import BaseTextField from 'common/components/base-text-field';
import FormProvider from 'common/components/form-provider';
import RequiredLabel from 'common/components/required-label';
import { styleInput } from 'common/constants';
import { eInvoiceUpgradeServiceSchema } from 'common/schema';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import numeral from 'plugins/numeral';
import { CreatePayment } from 'services/payment.service';
import { handleError } from 'utils/helpers';
import { toast } from 'react-toastify';
import { CheckPricePayment } from 'services/payment.service';
import { useLocation } from 'react-router-dom';

interface IProps {
    open: boolean;
    onClose: () => void;
    modalProps: {
        open: boolean;
        priceType: number;
        planType: string;
        monthlyPrice: number;
        yearlyPrice: number;
        priceText: string;
        choosePlan: {
            id: number;
            plan_type: string;
            price_type: string;
        };
    };
}

const ModalConfirmPayment: React.FC<IProps> = ({
    open,
    onClose,
    modalProps,
}) => {
    const steps = ['Bước 1', 'Bước 2'];
    const [activeStep, setActiveStep] = useState<number>(0);
    const [priceInfo, setPriceInfo] = useState({
        discount: 0,
        remainPackage: 0,
        totalBeforeTax: 0,
        vat: 0,
        total: 0,
    });
    const [isDisableNext, setIsDisableNext] = useState<boolean>(false);
    const location = useLocation();

    const moneySafeBetweenYearAndMonth =
        modalProps.priceType === 0
            ? 0
            : modalProps.yearlyPrice - modalProps.monthlyPrice * 12;
    const moneySafePercent =
        modalProps.priceType === 0
            ? 0
            : (
                  (moneySafeBetweenYearAndMonth / modalProps.yearlyPrice) *
                  100
              ).toFixed(0);

    const handleStep = (step: number) => {
        setActiveStep(step);
    };
    const methods = useForm({
        resolver: yupResolver(eInvoiceUpgradeServiceSchema),
        defaultValues: {
            companyName: '',
            taxCode: '',
            address: '',
            invoiceRecipient: '',
            email: '',
            phone: '',
        },
    });
    const onSubmit = methods.handleSubmit(async (data) => {
        if (modalProps.choosePlan.id === 0) {
            toast.error('Hệ thống không tìm thấy gói cần thanh toán');
            return;
        }
        try {
            const payload = {
                payment: {
                    product_type: 'Plan',
                    product_id: modalProps.choosePlan.id,
                    client_company_name: data.companyName,
                    client_tax_number: data.taxCode,
                    client_company_address: data.address,
                    client_name: data.invoiceRecipient,
                    client_phone: data.phone,
                    client_email: data.email,
                },
            };
            const response = await CreatePayment(payload);
            if (response.success) {
                const responseAttributes = response.data.payment.attributes;
                if (responseAttributes && responseAttributes.url_payment) {
                    toast.success('Hãy thanh toán để hoàn tất');
                    localStorage.setItem('urlCallback', location.pathname);
                    window.open(responseAttributes.url_payment, '_blank');
                } else {
                    toast.success('Thanh toán thành công');
                }
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            handleError(error);
        }
        onClose();
    });

    React.useEffect(() => {
        const fetchCheckPricePayment = async () => {
            try {
                const payload = {
                    payment: {
                        product_type: 'Plan',
                        product_id: modalProps.choosePlan.id,
                    },
                };
                const response = await CheckPricePayment(payload);

                if (!response.success) {
                    toast.error(response.message);
                    setIsDisableNext(true);
                }
                const responseAttributes = response.data.payment.attributes;
                const totalBeforeTax =
                    responseAttributes.price_unit -
                    responseAttributes.discount -
                    responseAttributes.product_overlap_discount;
                setPriceInfo({
                    discount: responseAttributes.discount,
                    remainPackage: responseAttributes.product_overlap_discount,
                    totalBeforeTax: totalBeforeTax < 0 ? 0 : totalBeforeTax,
                    vat: responseAttributes.tax,
                    total: responseAttributes.total_price,
                });
            } catch (error) {
                handleError(error);
            }
        };
        fetchCheckPricePayment();
    }, [modalProps.choosePlan.id]);

    return (
        <BaseDialog
            open={open}
            onClose={onClose}
            title={activeStep === 0 ? 'Xác nhận thanh toán' : 'Hóa đơn điện tử'}
            fullWidth={false}
            maxWidth={false}
            sxContent={{
                width: '978px',
                padding: '24px 32px',
            }}
        >
            <Box sx={{ width: '100%', mx: 'auto' }}>
                <Stepper nonLinear activeStep={activeStep} alternativeLabel>
                    {steps.map((label, index) => (
                        <Step key={index} completed={index < activeStep}>
                            <StepButton disableRipple>{}</StepButton>
                        </Step>
                    ))}
                </Stepper>
                <Box>
                    {steps[activeStep] === 'Bước 1' && (
                        <Box sx={{ mt: 3 }}>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    mb: 3,
                                }}
                            >
                                <Typography
                                    component={'p'}
                                    sx={{
                                        fontWeight: '700',
                                        color: '#000000',
                                        fontSize: '18px',
                                    }}
                                >
                                    Gói {modalProps.planType} theo{' '}
                                    {modalProps.priceType === 0
                                        ? 'tháng'
                                        : 'năm'}
                                    <br />
                                    {modalProps.priceType === 1 && (
                                        <span
                                            style={{
                                                color: '#000000',
                                                fontWeight: '400',
                                                fontSize: '14px',
                                            }}
                                        >
                                            Bạn đã tiết kiệm được{' '}
                                            {numeral(
                                                moneySafeBetweenYearAndMonth
                                            ).format()}{' '}
                                            VND ({moneySafePercent}%) so với gói
                                            tháng
                                        </span>
                                    )}
                                </Typography>
                                <Typography
                                    component={'p'}
                                    sx={{
                                        fontWeight: '700',
                                        textAlign: 'right',
                                        fontSize: '18px',
                                    }}
                                >
                                    {numeral(
                                        modalProps.priceType === 0
                                            ? modalProps.monthlyPrice
                                            : modalProps.yearlyPrice
                                    ).format()}{' '}
                                    VND
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    mb: 3,
                                }}
                            >
                                <Typography
                                    component={'p'}
                                    sx={{
                                        fontWeight: '700',
                                        color: '#000000',
                                        fontSize: '18px',
                                    }}
                                >
                                    Chiết khấu
                                </Typography>
                                <Typography
                                    component={'p'}
                                    sx={{
                                        fontWeight: '700',
                                        textAlign: 'right',
                                        fontSize: '18px',
                                    }}
                                >
                                    {numeral(priceInfo.discount).format()} VND
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    mb: 3,
                                }}
                            >
                                <Typography
                                    component={'p'}
                                    sx={{
                                        fontWeight: '700',
                                        color: '#000000',
                                        fontSize: '18px',
                                    }}
                                >
                                    Giá trị gói hiện tại còn lại
                                </Typography>
                                <Typography
                                    component={'p'}
                                    sx={{
                                        fontWeight: '700',
                                        textAlign: 'right',
                                        fontSize: '18px',
                                    }}
                                >
                                    {numeral(priceInfo.remainPackage).format()}{' '}
                                    VND
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    mb: 3,
                                }}
                            >
                                <Typography
                                    component={'p'}
                                    sx={{
                                        fontWeight: '700',
                                        color: '#000000',
                                        fontSize: '18px',
                                    }}
                                >
                                    Tổng chi phí trước thuế
                                </Typography>
                                <Typography
                                    component={'p'}
                                    sx={{
                                        fontWeight: '700',
                                        textAlign: 'right',
                                        fontSize: '18px',
                                    }}
                                >
                                    {numeral(priceInfo.totalBeforeTax).format()}{' '}
                                    VND
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    mb: 3,
                                }}
                            >
                                <Typography
                                    component={'p'}
                                    sx={{
                                        fontWeight: '700',
                                        color: '#000000',
                                        fontSize: '18px',
                                    }}
                                >
                                    VAT (10%)
                                </Typography>
                                <Typography
                                    component={'p'}
                                    sx={{
                                        fontWeight: '700',
                                        textAlign: 'right',
                                        fontSize: '18px',
                                    }}
                                >
                                    {numeral(priceInfo.vat).format()} VND
                                </Typography>
                            </Box>
                            <Divider sx={{ borderBottom: '2px dashed #000' }} />
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    mt: 3,
                                }}
                            >
                                <Typography
                                    component={'p'}
                                    sx={{
                                        fontWeight: '700',
                                        fontSize: '18px',
                                    }}
                                >
                                    Tổng chi phí
                                </Typography>
                                <Typography
                                    component={'p'}
                                    sx={{
                                        fontWeight: '700',
                                        textAlign: 'right',
                                        fontSize: '18px',
                                    }}
                                >
                                    {numeral(priceInfo.total).format()} VND
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    {steps[activeStep] === 'Bước 2' && (
                        <>
                            <FormProvider onSubmit={onSubmit} methods={methods}>
                                <Stack spacing={2} mt={3}>
                                    <Stack spacing={1}>
                                        <RequiredLabel
                                            required={false}
                                            label="Thông tin doanh nghiệp"
                                        />
                                        <BaseTextField
                                            sx={styleInput}
                                            name="companyName"
                                            autoComplete="off"
                                            placeholder="Tên doanh nghiệp"
                                        />
                                    </Stack>
                                    <Stack spacing={1}>
                                        <RequiredLabel
                                            required={false}
                                            label="Mã số thuế"
                                        />
                                        <BaseTextField
                                            sx={styleInput}
                                            name="taxCode"
                                            autoComplete="off"
                                            placeholder="Mã số thuế"
                                        />
                                    </Stack>
                                    <Stack spacing={1}>
                                        <RequiredLabel
                                            required={false}
                                            label="Địa chỉ doanh nghiệp"
                                        />
                                        <BaseTextField
                                            sx={styleInput}
                                            name="address"
                                            autoComplete="off"
                                            placeholder="Địa chỉ doanh nghiệp"
                                        />
                                    </Stack>
                                    <Stack spacing={1}>
                                        <RequiredLabel
                                            required={false}
                                            label="Tên người nhận hóa đơn"
                                        />
                                        <BaseTextField
                                            sx={styleInput}
                                            name="invoiceRecipient"
                                            autoComplete="off"
                                            placeholder="Tên người nhận hóa đơn"
                                        />
                                    </Stack>
                                    <Stack spacing={1}>
                                        <RequiredLabel
                                            required={false}
                                            label="Email nhận hóa đơn điện tử"
                                        />
                                        <BaseTextField
                                            sx={styleInput}
                                            name="email"
                                            autoComplete="off"
                                            placeholder="Email nhận hóa đơn điện tử"
                                        />
                                    </Stack>
                                    <Stack spacing={1}>
                                        <RequiredLabel
                                            required={false}
                                            label="Số điện thoại"
                                        />
                                        <BaseTextField
                                            sx={styleInput}
                                            name="phone"
                                            autoComplete="off"
                                            placeholder="Số điện thoại"
                                        />
                                    </Stack>
                                </Stack>
                                <Box
                                    mt={3}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        gap: 2,
                                    }}
                                >
                                    {activeStep === 1 && (
                                        <>
                                            <Button
                                                onClick={() => handleStep(0)}
                                                variant="outlined"
                                                sx={{
                                                    minWidth: '200px',
                                                    minHeight: '48px',
                                                    fontSize: '18px',
                                                    fontWeight: '700',
                                                    borderRadius: '48px',
                                                    textTransform: 'none',
                                                }}
                                            >
                                                Quay lại
                                            </Button>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                sx={{
                                                    minWidth: '200px',
                                                    minHeight: '48px',
                                                    fontSize: '18px',
                                                    fontWeight: '700',
                                                    borderRadius: '48px',
                                                    textTransform: 'none',
                                                }}
                                            >
                                                Thanh toán
                                            </Button>
                                        </>
                                    )}
                                </Box>
                            </FormProvider>
                        </>
                    )}
                </Box>
                <Box
                    mt={3}
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 2,
                    }}
                >
                    {activeStep === 0 && !isDisableNext && (
                        <Button
                            onClick={() => handleStep(1)}
                            variant="contained"
                            sx={{
                                minWidth: '200px',
                                minHeight: '48px',
                                fontSize: '18px',
                                fontWeight: '700',
                                borderRadius: '48px',
                                textTransform: 'none',
                            }}
                        >
                            Tiếp theo
                        </Button>
                    )}
                </Box>
            </Box>
        </BaseDialog>
    );
};

export default ModalConfirmPayment;
